import { AutogenLayoutDriverPayRateGroup } from "./autogen/AutogenLayoutDriverPayRateGroup";
import { DuplicateId, DuplicateIdProps } from "@mcleod/dispatch";
import {
    Button, ClickEvent, DataSource, DataSourceExecutionEvent, DataSourceMode, DataSourceModeChangeEvent,
    DataSourceValidationEvent, DataSourceValidationListener, Label, LookupModelSelectionEvent, Panel, SaveButton,
    SelectionMode, Snackbar, TableRow, TableRowDisplayEvent, TableRowExpansionEvent, TableSelectionEvent, Textbox
} from "@mcleod/components";
import { ModelRow, Navigation, UrlUtil } from "@mcleod/core";
import { DriverPayRateDetails } from "./DriverPayRateDetails";
import { DriverPayRateSlideout } from "./DriverPayRateSlideout";

export class DriverPayRateGroup extends AutogenLayoutDriverPayRateGroup {
    private duplicateIdProps: DuplicateIdProps = {
        tableName: "drs_payrate_group",
        screenName: this.titleSingular,
        navigateToUrl: `${this.layoutName}?mode=update&key=`,
        afterValidate: (textbox, props) => this.validateDriverPayRateGroup(textbox, props)
    };

    public parentDataSource: DataSource;
    public validationListener: DataSourceValidationListener = (event: DataSourceValidationEvent) => {
        this.validatePayRate(event);
    };
    public rateDetailsMap: Map<string, DriverPayRateDetails> = new Map<string, DriverPayRateDetails>();

    async onLoad(): Promise<void> {
        this.parentDataSource = this.mainDataSource;
        DuplicateId.configureIdTextbox(this.textboxId, this.duplicateIdProps);
        this.mainDataSource.addAfterModeChangeListener((event: DataSourceModeChangeEvent) => {
            if (event.newMode === DataSourceMode.ADD || event.newMode === DataSourceMode.UPDATE) {
                // Add selection listener to automatically expand rows when selected
                this.tableDriverPayRates.addSelectionListener((event: TableSelectionEvent) => {
                    this.onTableRowSelect(event);
                });
                this.tableDriverPayRates.addRowExpandListener(async (event: TableRowExpansionEvent) => {
                    // populate the details table layout
                    if (event.isExpanding) {
                        const tableRow: TableRow = event.target as TableRow;
                        const expandPanel = event.expandComponentParent as Panel;
                        const layoutRateDetails = expandPanel.findComponentById("layoutRateDetails") as DriverPayRateDetails;
                        const primaryKey = tableRow.data.get("primary_key");
                        if (!this.rateDetailsMap.has(primaryKey)) {
                            const rateDetailRows = tableRow.data.get("rate_details", []);
                            if (rateDetailRows) {
                                for (const [key, value] of Object.entries(rateDetailRows)) {
                                    let modelRow: ModelRow;
                                    if (value instanceof ModelRow) {
                                        modelRow = value.createBasicCopy();
                                        layoutRateDetails.mainDataSource.addRow(modelRow);
                                        modelRow.set("sequence", value.get("sequence"));
                                    } else {
                                        modelRow = new ModelRow(layoutRateDetails.tableDriverPayRateDetails.dataSource.url, false, value);
                                        layoutRateDetails.mainDataSource.addRow(modelRow);
                                    }
                                    if (modelRow.originalData?.["sequence"]) {
                                        modelRow.set("sequence", modelRow.originalData["sequence"]);
                                    }
                                }
                            }
                            this.rateDetailsMap.set(primaryKey, layoutRateDetails);
                        }
                        layoutRateDetails.initialize(tableRow.data, this.rateDetailsMap.get(primaryKey).sourceDrsPayrate.data);
                    }
                });
                this.tableDriverPayRates.addRowDisplayListener((event: TableRowDisplayEvent) => {
                    const tableRow: TableRow = event.getTableRow();
                    if (tableRow.data._appending) {
                        tableRow.setExpandedAsync(true);
                    }
                });
                if (event.newMode === DataSourceMode.UPDATE) {
                    this.dataheaderDriverPayRateGroup.showDelete = false;
                }
            } else if (event.newMode === DataSourceMode.SEARCH) {
                this.tableDriverPayRates.expandComponent = null; // Clear the expansion component
                this.tableDriverPayRates.selectionMode = SelectionMode.NONE;
            }
        });
        this.mainDataSource.addBeforeExecutionListener((event: DataSourceExecutionEvent) => {
            if (this.mainDataSource.mode === DataSourceMode.ADD) {
                this.sourceDrsPayrateHeader.data.forEach((row: ModelRow) => {
                    row.set("id", this.activeRow.get("id"));
                });
            }
            for (const [key, value] of this.rateDetailsMap) {
                if (this.mainDataSource.mode === DataSourceMode.ADD) {
                    value.sourceDrsPayrate.data.forEach((row: ModelRow) => {
                        row.set("pay_rate_group", this.activeRow.get("id"));
                    });
                }
                value.sourceDrsPayrate.parentDataSource = this.mainDataSource;
            }
        });
        this.sourceDrsPayrateHeader.addAfterExecutionListener((event: DataSourceExecutionEvent) => {
            const saveButton = this.owner?.dataHeader?.["saveButton"] ?? this.dataHeader?.["saveButton"] as SaveButton;
            if (saveButton) {
                saveButton.doOnDataSourceChanged(this.parentDataSource, false);
            }
            this.rateDetailsMap.clear();
        });
        this.mainDataSource.addValidationListener(this.validationListener);
        this.addLayoutDataLoadListener(() => {
            // Handle idToSelect URL parameter to automatically select a rate record
            const urlParams = UrlUtil.getPropsFromUrl(window.location.href);
            if (urlParams && urlParams.idToSelect) {
                this.selectRateRecordById(urlParams.idToSelect);
            }
        });
        this.buttonExpandAll.addClickListener(() => {
            this.tableDriverPayRates.rows.forEach((row: TableRow) => {
                if (!row.expanded) {
                    row.setExpandedAsync(true);
                }
            });
        });
        this.buttonCollapseAll.addClickListener(() => {
            this.tableDriverPayRates.rows.forEach((row: TableRow) => {
                if (row.expanded) {
                    row.setExpandedAsync(false);
                }
            });
        });

        this.buttonAddPayRate.addClickListener(async () => {
            DriverPayRateSlideout.openSlideout("add", {
                parentDataSource: this.parentDataSource,
                sourceDrsPayrateHeader: this.sourceDrsPayrateHeader,
                tableDriverPayRates: this.tableDriverPayRates,
                rateDetailsMap: this.rateDetailsMap
            });
        });
        this.textboxId.addLookupModelSelectionListener((event: LookupModelSelectionEvent) => {
            if (event.hasSelection()) {
                const selection = event.selection;
                this.sourceDrsPayrateHeader.data.forEach((row: ModelRow) => {
                    row.set("id", selection.get("id"));
                });
            }
        });
    }

    private async onTableRowSelect(event: TableSelectionEvent): Promise<void> {
        // Expand the newly selected row if one is selected
        if (event && event.newRow && event.newRow?.expanded === false) {
            await event.newRow.setExpandedAsync(true);
        }
    }

    private validateDriverPayRateGroup(textbox: Textbox, props: DuplicateIdProps): void {
        const content = new Panel();
        const text = new Label({
            text: "This Driver Pay Rate Group already exists that must be updated."
        });
        const drilldownButton = new Button({
            caption: "Open Record",
            onClick: () => {
                const url = `${this.layoutName}?mode=update&key=${textbox.getDataValue()}`;
                Navigation.navigateTo(url, { newTab: true });
            }
        });
        content.add(text);
        content.add(drilldownButton);
        props.displayDialogOnSave = true;
        Snackbar.showWarningSnackbar(content);
    }

    rateRowOnClick(event: ClickEvent) {
        // if row is already selected, then selection event will not be triggered
        const row = event.target as TableRow;
        if (row && row.data && !row.expanded && this.mainDataSource.mode !== DataSourceMode.SEARCH) {
            row.setExpandedAsync(true);
        } else if (this.mainDataSource.mode !== DataSourceMode.SEARCH && row.expanderButton.imageRotation == 0) {
            row.setExpandedAsync(false).then(() => {
                row.setExpandedAsync(true);
            });
        }
    }

    private rateRowOnDblClick(event: ClickEvent) {
        const row = event.target as TableRow;
        if (row && row.data && this.mainDataSource.mode !== DataSourceMode.SEARCH) {
            DriverPayRateSlideout.openSlideout("update", {
                selectedRow: row,
                parentDataSource: this.parentDataSource,
                sourceDrsPayrateHeader: this.sourceDrsPayrateHeader,
                tableDriverPayRates: this.tableDriverPayRates,
                rateDetailsMap: this.rateDetailsMap
            });
        }
    }

    async selectRateRecordById(idToSelect: string): Promise<void> {
        // Find the row that matches the idToSelect parameter
        const targetRow = this.tableDriverPayRates.rows.find(row => {
            return row.data.get("primary_key") === idToSelect;
        });

        if (targetRow) {
            // Select the row
            this.tableDriverPayRates.selectedRow = targetRow;

            // Expand the row if it's not already expanded (this will trigger the existing expansion logic)
            if (!targetRow.expanded) {
                await targetRow.setExpandedAsync(true);
            }
        }
    }

    private validatePayRate(event: DataSourceValidationEvent) {
        if (this.sourceDrsPayrateHeader.data.length === 0) {
            event.validationResults.push({
                validationMessage: "At least one Pay Rate must be defined for the group.",
                caption: "At least one Pay Rate must be defined for the group.",
                component: this,
                isValid: false
            });
        }
    }
}
